import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useState } from 'react';

import { Color } from '@/constants/color';
import { useRecentTaskStatuses, useTaskStatusLookup } from '@/hooks/useCeleryConsole';
import { TaskStatusRecord } from '@/types/celeryConsole';
import Button from '@/ui/components/Button';

import { ErrorDetail, isNotFound, Loading, PanelCard, StatusBadge } from '../../../shared';
import { PollInterval } from '../../../types';
import BadgeFilterRow from '../components/BadgeFilterRow';
import CopyableField from '../components/CopyableField';
import NameFilter from '../components/NameFilter';
import QueueBadge, { QUEUE_NAMES } from '../components/QueueBadge';
import TaskCell from '../components/TaskCell';
import { useToggleSet } from '../hooks/useToggleSet';

const COLUMN_COUNT = 6;
const SIZE_OPTIONS = [25, 50, 100, 250, 500, 1000] as const;
const STATUS_OPTIONS = ['PENDING', 'STARTED', 'SUCCESS', 'FAILURE', 'RETRY', 'REVOKED'];

type SortKey = 'id' | 'name' | 'queue' | 'status' | 'started_at' | 'runtime_seconds';
type SortDir = 'asc' | 'desc';

const SORT_GETTERS: Record<SortKey, (r: TaskStatusRecord) => string | number> = {
  id: (r) => r.id ?? '',
  name: (r) => r.name ?? '',
  queue: (r) => r.queue ?? '',
  status: (r) => r.status ?? '',
  started_at: (r) => r.started_at ?? 0,
  runtime_seconds: (r) => r.runtime_seconds ?? 0
};

const formatTime = (epoch: number | null): string => {
  if (!epoch) return '—';
  return new Date(epoch * 1000).toLocaleString();
};

const TaskDetailRow = ({ data, colSpan }: { data: TaskStatusRecord; colSpan: number }) => (
  <tr className="bg-gray-50">
    <td colSpan={colSpan} className="px-3 py-2">
      <div className="text-xs space-y-1">
        {data.args && <CopyableField label="args" value={data.args} className="font-mono" />}
        {data.kwargs && <CopyableField label="kwargs" value={data.kwargs} className="font-mono" />}
        <div>
          <span className="text-gray-500">finished:</span> {formatTime(data.finished_at)}
        </div>
        {data.error && <CopyableField label="error" value={data.error} className="text-red-600" />}
      </div>
    </td>
  </tr>
);

const LookupResult = ({ data }: { data: TaskStatusRecord }) => (
  <div className="rounded bg-gray-50 p-3 text-sm space-y-2">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-mono font-semibold">{data.name ?? data.id}</span>
      <StatusBadge status={data.status} />
      <QueueBadge queue={data.queue} />
      <span className="text-xs text-gray-500">{data.id}</span>
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      <div>
        <span className="text-gray-500">Runtime:</span> {data.runtime_display ?? '—'}
      </div>
      <div>
        <span className="text-gray-500">Started:</span> {formatTime(data.started_at)}
      </div>
      <div>
        <span className="text-gray-500">Finished:</span> {formatTime(data.finished_at)}
      </div>
    </div>
    {data.args && <CopyableField label="args" value={data.args} className="font-mono text-xs" />}
    {data.kwargs && (
      <CopyableField label="kwargs" value={data.kwargs} className="font-mono text-xs" />
    )}
    {data.error && (
      <div className="text-xs text-red-600 wrap-break-word">
        <span className="text-gray-500">error:</span> {data.error}
      </div>
    )}
  </div>
);

const SortHeader = ({
  label,
  column,
  current,
  onClick,
  align = 'left'
}: {
  label: string;
  column: SortKey;
  current: { key: SortKey; dir: SortDir };
  onClick: (key: SortKey) => void;
  align?: 'left' | 'right';
}) => {
  const active = current.key === column;
  const icon = !active ? faSort : current.dir === 'asc' ? faSortUp : faSortDown;
  return (
    <th
      className={`py-1 cursor-pointer select-none ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onClick(column)}>
      {label}{' '}
      <FontAwesomeIcon
        icon={icon}
        className={`text-xs ${active ? 'text-gray-700' : 'text-gray-400'}`}
      />
    </th>
  );
};

const RecentStatusPanel = ({ interval }: { interval: PollInterval }) => {
  const [pageSize, setPageSize] = useState<number>(50);
  const { data, isLoading, error } = useRecentTaskStatuses(pageSize, interval);
  const [nameFilter, setNameFilter] = useState('');
  const [idInput, setIdInput] = useState('');
  const [submittedId, setSubmittedId] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'id',
    dir: 'asc'
  });
  const expanded = useToggleSet<string>();
  const statuses = useToggleSet<string>();
  const queues = useToggleSet<string>();
  const lookup = useTaskStatusLookup(submittedId);
  const records = data ?? [];

  const toggleExpanded = (id: string | null) => {
    if (id) expanded.toggle(id);
  };

  const clearLookup = () => {
    setIdInput('');
    setSubmittedId('');
  };

  const onSortClick = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  // Two independent modes, each with one job:
  //   - nameFilter live-filters the recent slice already loaded (client-side
  //     substring on the dot path);
  //   - idInput + "Look up" hits the single-id endpoint so callers can fetch
  //     older ids that aged out of the recent feed.
  const trimmed = nameFilter.trim().toLowerCase();
  const filtered = records.filter((record) => {
    if (trimmed && !(record.name ?? '').toLowerCase().includes(trimmed)) return false;
    if (statuses.values.size > 0 && !statuses.values.has(record.status ?? '')) return false;
    if (queues.values.size > 0 && !queues.values.has(record.queue ?? '')) return false;
    return true;
  });
  // Stable sort on whatever column the user picked; default 'id' asc holds
  // rows in place across polls. localeCompare for strings, numeric subtraction
  // for numbers, with id as a tiebreaker so ties stay stable too.
  const getter = SORT_GETTERS[sort.key];
  const idGetter = SORT_GETTERS.id;
  const sorted = filtered.slice().sort((a, b) => {
    const av = getter(a);
    const bv = getter(b);
    let cmp: number;
    if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv));
    if (cmp === 0) cmp = String(idGetter(a)).localeCompare(String(idGetter(b)));
    return sort.dir === 'asc' ? cmp : -cmp;
  });
  const looking = Boolean(submittedId);

  return (
    <PanelCard title={`Recent task results — ${records.length}`}>
      <div className="flex items-center justify-end gap-2 mb-2 text-sm text-gray-500">
        Show last
        <select
          className="border rounded px-2 py-1 text-sm bg-white"
          value={pageSize}
          onChange={(event) => setPageSize(Number(event.target.value))}>
          {SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2 mb-3">
        <NameFilter
          value={nameFilter}
          onChange={setNameFilter}
          placeholder="Filter by task dot path (live, against this slice)…"
        />
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmittedId(idInput.trim());
          }}>
          <input
            className="border rounded px-2 py-1 text-sm flex-1"
            placeholder="Look up a specific task id (server-side)…"
            value={idInput}
            onChange={(event) => setIdInput(event.target.value)}
          />
          <Button type="submit" color={Color.BLUE}>
            Look up
          </Button>
          {(looking || idInput) && (
            <Button color={Color.GRAY} onClick={clearLookup}>
              Clear lookup
            </Button>
          )}
        </form>
        <BadgeFilterRow
          label="Status"
          options={STATUS_OPTIONS}
          selected={statuses.values}
          onToggle={statuses.toggle}
          onClear={statuses.clear}
          renderBadge={(status) => <StatusBadge status={status} />}
        />
        <BadgeFilterRow
          label="Queue"
          options={QUEUE_NAMES}
          selected={queues.values}
          onToggle={queues.toggle}
          onClear={queues.clear}
          renderBadge={(queue) => <QueueBadge queue={queue} />}
        />
      </div>

      {looking && (
        <div className="mb-3">
          {lookup.isLoading ? (
            <Loading />
          ) : lookup.data ? (
            <LookupResult data={lookup.data} />
          ) : lookup.error && !isNotFound(lookup.error) ? (
            <ErrorDetail error={lookup.error} />
          ) : (
            <p className="text-sm text-gray-500">No record for {submittedId}.</p>
          )}
        </div>
      )}

      {isLoading && !data ? (
        <Loading />
      ) : error ? (
        <ErrorDetail error={error} />
      ) : records.length === 0 ? (
        <p className="text-sm text-gray-500">No recorded runs yet.</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-gray-500">
          No matches in this slice — adjust filters or try Look up for a specific id.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 w-4" />
                <SortHeader label="Task" column="name" current={sort} onClick={onSortClick} />
                <SortHeader label="Queue" column="queue" current={sort} onClick={onSortClick} />
                <SortHeader label="Status" column="status" current={sort} onClick={onSortClick} />
                <SortHeader
                  label="Started"
                  column="started_at"
                  current={sort}
                  onClick={onSortClick}
                />
                <SortHeader
                  label="Runtime"
                  column="runtime_seconds"
                  current={sort}
                  onClick={onSortClick}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {sorted.map((record, index) => {
                const id = record.id ?? String(index);
                const isOpen = expanded.values.has(id);
                return (
                  <Fragment key={id}>
                    <tr
                      className="border-t align-top cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleExpanded(record.id)}>
                      <td className="py-2 text-xs text-gray-400">{isOpen ? '▼' : '▶'}</td>
                      <TaskCell name={record.name} id={record.id} />
                      <td className="py-2 whitespace-nowrap">
                        <QueueBadge queue={record.queue} />
                      </td>
                      <td className="py-2">
                        <StatusBadge status={record.status} />
                      </td>
                      <td className="py-2 text-xs whitespace-nowrap">
                        {formatTime(record.started_at)}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {record.runtime_display ?? '—'}
                      </td>
                    </tr>
                    {isOpen && <TaskDetailRow data={record} colSpan={COLUMN_COUNT} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PanelCard>
  );
};

export default RecentStatusPanel;
